import React, { useCallback, useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { useAsync, useEventSlug } from 'hooks'
import { colors, _ } from 'utils'
import { getNodes } from 'graphql/utils'
import { PickNode } from 'graphql/types'
import { ShowType, VirtualSpacesQuery } from 'generated/graphql'
import DateField from 'features/forms/components/DateField'
import TimeField from 'features/forms/components/TimeField'
import { getSpaces } from '../graphql/query'
import Switch from '../../forms/components/Switch'
import SelectField from '../../forms/components/SelectField'
import TextField from '../../forms/components/TextField'
import FileUpload, { IMAGE_ONLY } from '../../forms/components/FileUpload'

const S = {
    Title: styled.p`
        width: 100%;
        padding-left: 10px;
        font-weight: 700;
        font-size: 20px;
        color: ${colors.grayTitleOpacity(0.9)};
    `,
    Description: styled.p`
        width: 100%;
        padding-left: 10px;
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
}

const VirtualEvent = () => {
    const eventSlug = useEventSlug()
    const getSpacesAsync = useCallback(
        () => getSpaces({ first: 499, eventId: eventSlug }),
        [eventSlug]
    )
    const [isFirstLoad, setIsFirstLoad] = useState(false)
    const [selectedSpace, setSelectedSpace] = useState<PickNode<VirtualSpacesQuery, 'spaces'>>(null)
    const { value } = useAsync(getSpacesAsync)
    const { initialValues, setFieldValue, values } = useFormikContext<ShowType>()
    const hasLoggedOn = initialValues?.moderator?.hasLoggedOn
    const disabledStreamUrl = selectedSpace?.presentationFields?.streamIsContinues
    const disabledEmbeddedUrl = selectedSpace?.presentationFields?.interactionIsContinues

    useEffect(() => {
        if (values?.showAttendees === undefined && values?.enableChat === undefined) {
            setIsFirstLoad(true)
        }
    }, [values?.enableChat, values?.showAttendees])

    useEffect(() => {
        if (selectedSpace && isFirstLoad) {
            setFieldValue('enableChat', selectedSpace.enableChat)
            setFieldValue('showAttendees', selectedSpace?.presentationFields?.showAttendees)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpace])

    return (
        <>
            <SelectField
                name="virtualSpaceId"
                label={_('show.form.virtual_space')}
                options={getNodes(value?.data?.event?.spaces) || []}
                optionValue="id"
                optionLabel="name"
                lg={1/2}
                onSelect={setSelectedSpace}
            />
            <TextField
                name="moderatorEmail"
                label={_('show.form.moderator_email')}
                lg={1/2}
                isActiveStatus={hasLoggedOn}
                status={!initialValues?.moderator ? '' : (hasLoggedOn
                    ? _('show.form.moderator_logged_status')
                    : _('show.form.moderator_pending_status'))}
                statusHelperText={hasLoggedOn
                    ? _('show.form.moderator_logged_helper_text')
                    : _('show.form.moderator_pending_helper_text')}
            />
            <TextField
                name="streamUrl"
                label={_('show.form.streamUrl')}
                lg={1/2}
                isDisabled={disabledStreamUrl}
                highLightHelperText={disabledStreamUrl && _('show.form.disabled_streamUrl_helper_text')}
            />
            <TextField
                name="embeddedUrl"
                label={_('show.form.embeddedUrl')}
                lg={1/2}
                isDisabled={disabledEmbeddedUrl}
                highLightHelperText={disabledEmbeddedUrl && _('show.form.disabled_embeddedUrl_helper_text')}
            />
            <Switch
                name="showAttendees"
                label={_('show.form.show_attendees')}
                helperText={_('show.form.show_attendees_helper_text')}
            />
            <FileUpload
                name="virtualImage"
                label={_('show.form.space_image')}
                accept={IMAGE_ONLY}
                maxSize={25} // in Mbs
                placeholder={_('show.form.space_image_place_holder')}
                maxImageSize={{ width: 1000, height: 500 }}
                previewImagePlacement="top-end"
            />
            <S.Title>{_('show.form.replay_header')}</S.Title>
            <S.Description>{_('show.form.replay_description')}</S.Description>
            <TextField
                name="playbackUrl"
                label={_('show.form.playback_url')}
                showInfoIcon
                helperText={_('show.form.playback_url.helper_text')}
            />
            <DateField
                name="releaseDate"
                label={_('show.form.releaseDate')}
                lg={1/2}
            />
            <TimeField
                name="releaseDate"
                label={_('show.form.releaseTime')}
                lg={1/2}
            />
        </>
    )
}

export default VirtualEvent
